import { GetStaticPropsResult, NextPage } from 'next';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import { Button } from '@prickle/next-ui';
import { RichText } from 'prismic-reactjs';
import { Layout } from '../../components';
import { i18nConfig } from '../../next-i18next.config';
import { AssignmentProps, Locale } from '../../types';
import { getAssignments } from '../../utils/prismic/assignments';
import getPrismicMenuByLocale from '../../utils/prismic/menu';
import getPrismicOptionsByLocale from '../../utils/prismic/options';
import { REVALIDATION_TIME } from '../../utils/constants';
import Link from 'next/link';

const AssignmentsPage: NextPage<AssignmentProps> = ({
  options,
  menu,
  assignments,
}) => {
  const { t } = useTranslation('projects');
  return (
    <>
      <NextSeo
        title="Prickle | Assignments"
        description="Prickle | Assignments"
      />
      <Layout options={options} menu={menu}>
        <div className="font-body">
          <div className="grid justify-between gap-20 pt-12 bg-white md:pt-0 md:grid-cols-2">
            <div className="h-full px-6 mx-auto">
              <h1 className="my-10 text-5xl font-medium text-center lg:text-6xl font-heading md:text-left">
                {t('projectsTitle')}
              </h1>
              <p className="py-4 text-lg text-black">{t('projectsSubtitle')}</p>
              <p className="py-4 text-lg text-black">
                <Trans i18nKey="projectsDescription1" ns={'projects'}>
                  Aplica a proyectos en diferentes sectores de la
                  tecnologia.Abajo encontraras los proyectos activos. Aplica y
                  realiza tu sueño como desarrollador,
                  <span className="italic font-bold">
                    #Full Stack, #Backend, #Frontend, #experto en Java, #DevOps,
                    #proyectos Web #Mobile
                  </span>
                  y un sinfin de tecnologias mas
                </Trans>
              </p>
            </div>
            <div className="hidden h-full md:flex">
              <Image
                alt="Developer at work"
                width={1350}
                height={900}
                className="object-cover object-right h-full"
                src="https://images.prismic.io/prickle-website/1d79e04b-4f4c-4a64-a201-5deff7180b61_web_dev.jpg?auto=compress,format"
              ></Image>
            </div>
          </div>
          <div className="mx-auto my-16 max-w-7xl">
            <h2 className="mb-16 text-4xl font-medium text-center text-white uppercase lg:text-6xl font-heading">
              {t('projectsListings')}
            </h2>
            {assignments.length === 0 && (
              <p className="px-8 text-xl text-center text-white">
                {t('projectsMissing')}
              </p>
            )}
            {assignments.length > 0 && (
              <div className="grid gap-6 px-6 md:grid-cols-2">
                {assignments.map((assignment) => (
                  <Link
                    href={`assignments/${assignment.uid}`}
                    key={assignment.uid}
                  >
                    <div className="flex items-center justify-between px-8 py-6 rounded-lg shadow-lg bg-darkGray">
                      <div className="flex flex-col ">
                        <span className="text-xl font-medium text-white">
                          {RichText.asText(assignment.data.title)}
                        </span>
                        <span className="text-white text-primary-100">
                          {' '}
                          {RichText.asText(assignment.data.excerpt)}
                        </span>
                      </div>
                      <div></div>
                      <div>
                        <Button
                          color="primary"
                          className="px-4 py-2 text-sm font-medium text-white rounded-lg shadow-lg"
                        >
                          {t('projectsView')}
                        </Button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export const getStaticProps = async ({
  previewData,
  locale,
}: {
  locale: Locale;
  previewData: any;
}): Promise<GetStaticPropsResult<AssignmentProps>> => {
  const options = await getPrismicOptionsByLocale(locale, previewData);
  const menu = await getPrismicMenuByLocale(locale, previewData);
  let assignments;
  try {
    assignments = await getAssignments(locale, previewData);
  } catch (error: any) {
    throw new Error(error);
  }
  return {
    revalidate: REVALIDATION_TIME,
    props: {
      ...(await serverSideTranslations(
        locale,
        ['projects', 'common', 'form'],
        i18nConfig,
      )),
      menu,
      options,
      assignments: assignments.results,
    },
  };
};

export default AssignmentsPage;
